import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Typography,
  Paper,
  InputAdornment,
  Grid2
} from "@mui/material"
import { type LabelLayoutSettingsValues, AlignmentType, TextWrapType, OrientationType } from "./types"
import {
  ExpandMore as ExpandMoreIcon,
  AspectRatio as AspectRatioIcon,
  TextFields as TextFieldsIcon,
  FormatAlignJustify as FormatAlignJustifyIcon
} from "@mui/icons-material"
import { ChipStyled, Stack } from "src/shared/components"
import OutlinedInput from "src/shared/components/outlined-input"
import { useState } from "react"
import { getDefaultLabelSettings } from "./barcodes-utils"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { alignmentOptions, nameWrapOptions, orientationOptions } from "./barcodes-utils"
import MinimulTabs from "src/shared/components/MinimalTabs/MinimulTabs"
const LabelLayoutSettings = ({
  config,
  onConfigChange
}: {
  config: LabelLayoutSettingsValues
  onConfigChange: (config: LabelLayoutSettingsValues) => void
}) => {
  const labelSettings = config ?? getDefaultLabelSettings()
  const [labelSettingsExpanded, setLabelSettingsExpanded] = useState(false)

  const handleLabelSettingChange = (key: keyof LabelLayoutSettingsValues, value: number | string) => {
    const newSettings = { ...labelSettings, [key]: value }
    onConfigChange({ ...newSettings })
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Accordion
        expanded={labelSettingsExpanded}
        onChange={(_, isExpanded) => setLabelSettingsExpanded(isExpanded)}
        sx={{ borderRadius: 2, border: (theme) => `1px solid ${theme?.palette?.divider}` }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          // sx={{ backgroundColor: (theme) => theme.palette?.action.disabledBackground }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AspectRatioIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              Label Layout Settings
            </Typography>
            <ChipStyled label="Printer Configuration" size="small" color="primary" variant="outlined" sx={{ ml: 2 }} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid2 container spacing={3}>
            {/* Label Dimensions */}
            <Grid2 size={{ xs: 12 }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: (theme) => theme?.palette?.secondary?.light, borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <AspectRatioIcon fontSize="small" />
                  Label Dimensions
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Width"
                      type="number"
                      value={labelSettings.labelWidth}
                      onChange={(e) => handleLabelSettingChange("labelWidth", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Height"
                      type="number"
                      value={labelSettings.labelHeight}
                      onChange={(e) => handleLabelSettingChange("labelHeight", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Padding"
                      type="number"
                      value={labelSettings.padding}
                      onChange={(e) => handleLabelSettingChange("padding", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <Box>
                      <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                        Zoom: {Math.round(labelSettings.zoom * 100)}%
                      </Typography>
                      <Slider
                        value={labelSettings.zoom}
                        min={0.5}
                        max={2.0}
                        step={0.1}
                        onChange={(_, value) => handleLabelSettingChange("zoom", value as number)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                      />
                    </Box>
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <InputAutocomplete
                      label="Orientation"
                      value={orientationOptions.find((option) => option.value === labelSettings.orientation)}
                      onChange={(value: any) => handleLabelSettingChange("orientation", value.value as OrientationType)}
                      options={orientationOptions}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Column Gap"
                      type="number"
                      value={labelSettings.columnGap}
                      onChange={(e) => handleLabelSettingChange("columnGap", parseFloat(e.target.value) || 0)}
                      disabled={labelSettings.columns === 1}
                      endAdornment={
                        <InputAdornment position="end">
                          <FormatAlignJustifyIcon fontSize="small" />
                        </InputAdornment>
                      }
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <Stack direction="row" alignItems="flex-end" sx={{ width: "100%", height: "100%" }}>
                      <MinimulTabs
                        tabs={[1, 2, 3, 4].map((option) => ({
                          value: option,
                          label: option.toString()
                        }))}
                        sx={{ width: "100%" }}
                        value={labelSettings.columns}
                        handleTabChange={(value: number) => {
                          handleLabelSettingChange("columns", value)
                        }}
                      />
                    </Stack>
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* Margins */}
            <Grid2 size={{ xs: 12 }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: (theme) => theme?.palette?.secondary?.light, borderRadius: 2 }}
              >
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  Margins
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Top"
                      type="number"
                      value={labelSettings.marginTop}
                      onChange={(e) => handleLabelSettingChange("marginTop", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Bottom"
                      type="number"
                      value={labelSettings.marginBottom}
                      onChange={(e) => handleLabelSettingChange("marginBottom", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Left"
                      type="number"
                      value={labelSettings.marginLeft}
                      onChange={(e) => handleLabelSettingChange("marginLeft", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 3 }}>
                    <OutlinedInput
                      label="Right"
                      type="number"
                      value={labelSettings.marginRight}
                      onChange={(e) => handleLabelSettingChange("marginRight", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* QR Code Settings */}
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: (theme) => theme?.palette?.secondary?.light, borderRadius: 2 }}
              >
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  QR Code
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <OutlinedInput
                      label="QR Code Size"
                      type="number"
                      value={labelSettings.qrCodeSize}
                      onChange={(e) => handleLabelSettingChange("qrCodeSize", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <OutlinedInput
                      label="Space After QR"
                      type="number"
                      value={labelSettings.qrCodeMarginRight}
                      onChange={(e) => handleLabelSettingChange("qrCodeMarginRight", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">mm</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <InputAutocomplete
                      label="QR Code Alignment"
                      value={alignmentOptions.find((option) => option.value === labelSettings.qrCodeAlignment)}
                      onChange={(value: any) =>
                        handleLabelSettingChange("qrCodeAlignment", value.value as AlignmentType)
                      }
                      options={alignmentOptions}
                    />
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* Text Sizes */}
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: (theme) => theme?.palette?.secondary?.light, borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <TextFieldsIcon fontSize="small" />
                  Text Sizes
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <OutlinedInput
                      label="Product Name"
                      type="number"
                      value={labelSettings.productNameFontSize}
                      onChange={(e) => handleLabelSettingChange("productNameFontSize", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">px</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <OutlinedInput
                      label="Batch Number"
                      type="number"
                      value={labelSettings.batchNumberFontSize}
                      onChange={(e) => handleLabelSettingChange("batchNumberFontSize", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">px</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12 }}>
                    <OutlinedInput
                      label="Expiry Date"
                      type="number"
                      value={labelSettings.expiryFontSize}
                      onChange={(e) => handleLabelSettingChange("expiryFontSize", parseFloat(e.target.value) || 0)}
                      endAdornment={<InputAdornment position="end">px</InputAdornment>}
                      fullWidth
                    />
                  </Grid2>
                </Grid2>
              </Paper>
            </Grid2>

            {/* Text Alignment & Wrapping */}
            <Grid2 size={{ xs: 12 }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: (theme) => theme?.palette?.secondary?.light, borderRadius: 2 }}
              >
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  Text Alignment & Wrapping
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, sm: 6, md: labelSettings.productNameWrap === "multi" ? 4 : 6 }}>
                    <InputAutocomplete
                      label="Text Alignment"
                      value={alignmentOptions.find((option) => option.value === labelSettings.textAlignment)}
                      onChange={(value: any) => handleLabelSettingChange("textAlignment", value.value as AlignmentType)}
                      options={alignmentOptions}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: labelSettings.productNameWrap === "multi" ? 4 : 6 }}>
                    <InputAutocomplete
                      label="Product Name Wrap"
                      value={nameWrapOptions.find((option) => option.value === labelSettings.productNameWrap)}
                      onChange={(value: any) =>
                        handleLabelSettingChange("productNameWrap", value.value as TextWrapType)
                      }
                      options={nameWrapOptions}
                    />
                  </Grid2>
                  {labelSettings.productNameWrap === "multi" && (
                    <Grid2 size={{ xs: 12, sm: 6, md: labelSettings.productNameWrap === "multi" ? 4 : 6 }}>
                      <OutlinedInput
                        label="Max Lines"
                        type="number"
                        value={labelSettings.productNameMaxLines}
                        onChange={(e) => handleLabelSettingChange("productNameMaxLines", parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1, max: 5 }}
                        fullWidth
                        helperText="Maximum number of lines for product name"
                      />
                    </Grid2>
                  )}
                </Grid2>
              </Paper>
            </Grid2>
          </Grid2>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default LabelLayoutSettings
